import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, HandCoins, MessageCircle, PencilLine, Printer } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, saleWaText, waLink } from "@/lib/api";
import { DeliveryBadge, ErrorBox, Field, Modal, Money, PayBadge, Sel } from "@/components/common";

export function SaleInfoDialog({ open, onOpenChange, sale, onDone }) {
  const { settings } = useAuth();
  const [f, setF] = useState({ category: sale.category || "", notes: sale.notes || "" });
  const [err, setErr] = useState("");
  const go = async () => {
    try { await api.post(`/sales/${sale.id}/info`, f); toast.success("تم حفظ التعديل"); onOpenChange(false); onDone?.(); } catch (e) { setErr(errMsg(e)); }
  };
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={`تعديل بيانات البيع ${sale.number}`} description="الفئة والملاحظات (التعديلات المالية تتم من «تعديل السعر» بصلاحية المدير)"
      footer={<button className="k-btn-primary" onClick={go} data-testid="sale-info-save">حفظ</button>}>
      <Field label="الفئة"><Sel value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="sale-info-category"><option value="">— بدون فئة —</option>{(settings?.sale_categories || []).map((c) => <option key={c}>{c}</option>)}</Sel></Field>
      <Field label="ملاحظات"><textarea className="k-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="sale-info-notes" /></Field>
      <ErrorBox text={err} />
    </Modal>
  );
}

export function SaleActionButtons({ sale, onEdit, compact }) {
  const { can, settings } = useAuth();
  const nav = useNavigate();
  const cls = compact ? "k-btn-outline k-btn-sm" : "k-btn-outline";
  const active = sale.status === "active";
  return (
    <div className="flex flex-wrap gap-2">
      <Link className={cls} to={`/sales/${sale.id}`} data-testid={`action-details-${sale.number}`}><Eye className="w-4 h-4" /> التفاصيل</Link>
      {active && can("record_payment") && sale.remaining > 0 && <button className={compact ? "k-btn-primary k-btn-sm" : "k-btn-primary"} onClick={() => nav(`/sales/${sale.id}?pay=1`)} data-testid={`action-pay-${sale.number}`}><HandCoins className="w-4 h-4" /> إضافة دفعة</button>}
      {active && can("create_sale", "edit_price") && onEdit && <button className={cls} onClick={onEdit} data-testid={`action-edit-${sale.number}`}><PencilLine className="w-4 h-4" /> تعديل</button>}
      <Link className={cls} to={`/print/sale/${sale.id}`} target="_blank" data-testid={`action-print-${sale.number}`}><Printer className="w-4 h-4" /> طباعة</Link>
      <a className={`${cls} text-emerald-700`} href={waLink(sale.customer_phone, saleWaText(sale, settings?.org_name))} target="_blank" rel="noreferrer" data-testid={`action-wa-${sale.number}`}><MessageCircle className="w-4 h-4" /> واتساب</a>
    </div>
  );
}

export default function SaleActionsDialog({ sale, onClose, onChanged }) {
  const [edit, setEdit] = useState(false);
  if (!sale) return null;
  return (
    <>
      <Modal open={!!sale && !edit} onOpenChange={(o) => !o && onClose()} title={`${sale.customer_name} – ${sale.number}`} description={`رقم الكبش ${sale.ear_tag} · ${sale.section_name}${sale.category ? ` · ${sale.category}` : ""}`}>
        <div data-testid="sale-actions-dialog" className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-[#F6F1E7] p-2"><div className="text-xs text-slate-500">الإجمالي</div><Money v={sale.total} /></div>
            <div className="rounded-xl bg-emerald-50 p-2"><div className="text-xs text-slate-500">المدفوع</div><Money v={sale.paid_total} className="text-emerald-700" /></div>
            <div className="rounded-xl bg-rose-50 p-2"><div className="text-xs text-slate-500">المتبقي</div><Money v={sale.remaining} className="text-rose-700" /></div>
          </div>
          <div className="flex gap-2"><PayBadge s={sale.payment_status} /><DeliveryBadge s={sale.delivery_status} /></div>
          <SaleActionButtons sale={sale} onEdit={() => setEdit(true)} />
        </div>
      </Modal>
      {edit && <SaleInfoDialog open onOpenChange={(o) => { if (!o) { setEdit(false); onClose(); } }} sale={sale} onDone={onChanged} />}
    </>
  );
}
